// A blood gas card, printed the way a blood gas analyser prints it: a header with the sample type and the oxygen the patient
// was on, then the gas values (pH, pCO2, pO2), the acid–base values (HCO3⁻, base excess) and lactate, then the electrolytes
// and glucose, each with its unit and reference range and an arrow beside a value out of range. Only what the data has:
// the sample type comes from the card's title or the sentence that shows the gas ("Arterial gas shown", "(venous pH)"), the
// oxygen only from words tied to the gas itself ("blood gas on air", "ABG on 28% oxygen", "PaO2 on room air"), and neither
// is printed when the data does not say. A card with anything an analyser does not measure (urea, ketones, a full blood
// count) stays a lab slip.
import type { BloodsGraphic, BloodsRow } from '@shared/types';

export type GasSample = 'Arterial' | 'Venous' | 'Capillary';

/** One printed line of the analyser's report, with the card row it stands for (`index`, so a tap still answers that row). */
export interface GasLine {
  index: number;
  row: BloodsRow;
  /** As the analyser prints it: pH, pCO2, HCO3⁻, BE, Na⁺… */
  label: string;
  /** The name said aloud. */
  spoken: string;
  /** The value as printed (a true minus sign). */
  value: string;
  /** The reference range as the analyser prints it, or '' when the card has none. */
  range: string;
  /** 0: gas, acid–base and lactate; 1: electrolytes, glucose and the rest, printed under a rule. */
  block: 0 | 1;
}

export interface GasPrintout {
  sample?: GasSample;
  /** The oxygen the sample was taken on, e.g. 'FiO2 0.21 (room air)', 'FiO2 0.28', 'O2 15 L/min'. */
  oxygen?: string;
  lines: GasLine[];
}

interface Param { re: RegExp; label: string; spoken: string; block: 0 | 1; core?: boolean }
/** What a blood gas analyser reports, in the order it prints them. */
const PARAMS: readonly Param[] = [
  { re: /^ph$/i, label: 'pH', spoken: 'pH', block: 0 },
  { re: /^p(a)?co2$/i, label: 'pCO2', spoken: 'pCO2', block: 0, core: true },
  { re: /^p(a)?o2$/i, label: 'pO2', spoken: 'pO2', block: 0, core: true },
  { re: /^(s|sa|sv)o2$|^oxygen saturation$/i, label: 'sO2', spoken: 'Oxygen saturation', block: 0 },
  { re: /^(bicarbonate|standard bicarbonate|hco3-?)( \((abg|vbg|arterial|venous)\))?$/i, label: 'HCO3⁻', spoken: 'Bicarbonate', block: 0, core: true },
  { re: /^(base excess|be)$/i, label: 'BE', spoken: 'Base excess', block: 0, core: true },
  { re: /^lactate$/i, label: 'Lactate', spoken: 'Lactate', block: 0 },
  { re: /^(sodium|na)$/i, label: 'Na⁺', spoken: 'Sodium', block: 1 },
  { re: /^(potassium|k)$/i, label: 'K⁺', spoken: 'Potassium', block: 1 },
  { re: /^(chloride|cl)$/i, label: 'Cl⁻', spoken: 'Chloride', block: 1 },
  { re: /^(ionised calcium|ionized calcium|ica)$/i, label: 'Ca²⁺', spoken: 'Ionised calcium', block: 1 },
  { re: /^anion gap$/i, label: 'Anion gap', spoken: 'Anion gap', block: 1 },
  { re: /^glucose$/i, label: 'Glucose', spoken: 'Glucose', block: 1 },
  { re: /^(haemoglobin|hemoglobin|hb|thb)$/i, label: 'Hb', spoken: 'Haemoglobin', block: 1 },
  { re: /^(carboxyhaemoglobin|cohb)$/i, label: 'COHb', spoken: 'Carboxyhaemoglobin', block: 1 },
  { re: /^(methaemoglobin|methb)$/i, label: 'MetHb', spoken: 'Methaemoglobin', block: 1 },
];
const paramOf = (test: string) => {
  const t = (test ?? '').trim();
  const i = PARAMS.findIndex((p) => p.re.test(t));
  return i < 0 ? undefined : { ...PARAMS[i], order: i };
};

/** A title that names a gas (ABG, VBG, blood gas, arterial…). */
const GAS_TITLE = /\b(abg|vbg|cbg|(blood )?gas(es)?|arterial|venous|capillary)\b/i;

/**
 * The sentences of a stem that show the card ("Blood gas shown.", "Results are shown (venous pH)."). A sentence ends at . ? or !
 * followed by a space, so a decimal (FiO2 0.4) never splits one; no lookbehind, which older iPhones' Safari cannot parse.
 */
function showingSentences(stem: string): string {
  return stem.replace(/([.?!])\s+/g, '$1\u0000').split('\u0000').filter((s) => /\b(shown|below|attached|as follows)\b/i.test(s)).join(' ');
}

function sampleIn(text: string): GasSample | undefined {
  if (/\b(venous|vbg)\b/i.test(text)) return 'Venous';
  if (/\b(capillary|cbg)\b/i.test(text)) return 'Capillary';
  if (/\b(arterial|abg|pao2|paco2)\b/i.test(text)) return 'Arterial';
  return undefined;
}

/** "gas on air", "ABG on 28% oxygen", "PaO2 on room air", "repeat ABG on 15 L/min O2", "gas on FiO2 0.4": the oxygen tied to the gas. */
const ON_OXYGEN = /\b(?:gas|gases|abg|vbg|cbg|pao2|po2)\b(?:\s+(?:is|was|taken|drawn|sample))*\s+on\s+(?:(room air|air)\b|(\d{2,3})\s?%\s*(?:oxygen|o2)\b|(\d{1,2}(?:\.\d)?)\s?(?:l|litres?)(?:\/min|\s+per\s+minute|\/minute)?\s+(?:of\s+)?(?:oxygen|o2)\b|fio2\s*(?:of\s*)?(0?\.\d{1,2}|1(?:\.0{1,2})?)\b)/i;

/** The oxygen the gas was taken on, as an analyser's header prints it, or undefined when the words do not say. */
export function oxygenIn(text: string): string | undefined {
  const m = ON_OXYGEN.exec(text);
  if (!m) return undefined;
  if (m[1]) return 'FiO2 0.21 (room air)';
  if (m[2]) { const pct = Number(m[2]); return pct >= 21 && pct <= 100 ? `FiO2 ${(pct / 100).toFixed(2)}${pct === 21 ? ' (room air)' : ''}` : undefined; }
  if (m[3]) { const flow = Number(m[3]); return flow > 0 && flow <= 60 ? `O2 ${m[3]} L/min` : undefined; }
  if (m[4]) { const f = Number(m[4]); return f >= 0.21 && f <= 1 ? `FiO2 ${f.toFixed(2)}${f === 0.21 ? ' (room air)' : ''}` : undefined; }
  return undefined;
}

const decimals = (x: string) => (x.includes('.') ? x.split('.')[1].length : 0);
const minus = (x: string) => x.replace(/^-/, '−');

/**
 * A reference range as an analyser prints it, in brackets: the card's range with the lab's notes for a sample on air or a random
 * glucose dropped (the header says what the sample was taken on), padded to the value's decimals ("4.7–6" beside 9.8 → [4.7–6.0])
 * and "−2 to +2" where a bound is negative. Anything else is printed as it is; no range, nothing.
 */
export function gasRange(range: string, value: string): string {
  const r = (range ?? '').replace(/\s*\((on air|random)\)\s*/gi, ' ').trim();
  if (!r || r === '—' || r === '-') return '';
  const m = /^(-?\d+(?:\.\d+)?)\s*[–-]\s*(-?\d+(?:\.\d+)?)$/.exec(r);
  if (!m) return `[${r}]`;
  const d = Math.max(decimals(m[1]), decimals(m[2]), decimals(String(value ?? '').trim().replace(/^[<>−-]/, '')));
  const lo = Number(m[1]), hi = Number(m[2]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) return `[${r}]`;
  const f = (x: number) => x.toFixed(Math.min(d, 3));
  return lo < 0 ? `[${minus(f(lo))} to ${hi > 0 ? '+' : ''}${minus(f(hi))}]` : `[${f(lo)}–${f(hi)}]`;
}

/**
 * The analyser's printout for a bloods card, or undefined when the card is not a blood gas. A blood gas has a pH and nothing an
 * analyser does not measure, and either a title that names a gas or one of pCO2, pO2, bicarbonate or base excess beside the pH.
 * `stem` (the question) gives the sample type and the oxygen when it says them where it shows the gas.
 */
export function gasPrintout(graphic: BloodsGraphic, stem?: string): GasPrintout | undefined {
  const rows = Array.isArray(graphic.rows) ? graphic.rows : [];
  const params = rows.map((r) => paramOf(r?.test ?? ''));
  if (!rows.length || params.some((p) => !p) || !params.some((p) => p!.label === 'pH')) return undefined;
  if (!GAS_TITLE.test(graphic.title ?? '') && !params.some((p) => p!.core)) return undefined;
  const showing = showingSentences(stem ?? '');
  const sample = sampleIn(showing) ?? sampleIn(graphic.title ?? '');
  const oxygen = oxygenIn(showing);
  const lines = rows
    .map((row, index): GasLine & { order: number } => {
      const p = params[index]!;
      return { index, row, label: p.label, spoken: p.spoken, value: minus(String(row.value ?? '').trim()), range: gasRange(row.range, row.value), block: p.block, order: p.order };
    })
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ order: _order, ...line }) => line);
  return { ...(sample ? { sample } : {}), ...(oxygen ? { oxygen } : {}), lines };
}

/** The printout's header line: the sample type and the oxygen, as the analyser prints them ("Arterial · FiO2 0.21 (room air)"). */
export const sampleLine = (g: Pick<GasPrintout, 'sample' | 'oxygen'>): string => [g.sample, g.oxygen].filter(Boolean).join(' · ');
